import React, { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetServiceQuery,
  useGetHomepageSettingsQuery,
} from "../redux/services/api";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingButtons from "../components/FloatingButtons";
import { useTranslation } from "../hooks/useTranslation";
import { useLanguage } from "../contexts/LanguageContext";
import { trackServicePage } from "../utils/analytics";
import AOS from "aos";
import "aos/dist/aos.css";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

// Helper function to parse Draft.js content to HTML
const parseDraftContent = (content) => {
  if (!content) return "";

  try {
    // If content is already a string, return it
    if (typeof content === "string") {
      // Check if it's JSON string
      if (content.startsWith("{") && content.endsWith("}")) {
        const parsed = JSON.parse(content);
        return parseDraftBlocks(parsed);
      }
      return content;
    }

    // If content is an object, parse it directly
    if (typeof content === "object") {
      return parseDraftBlocks(content);
    }

    return content;
  } catch (error) {
    console.error("Error parsing content:", error);
    return content;
  }
};

const parseDraftBlocks = (draftContent) => {
  if (!draftContent.blocks || !Array.isArray(draftContent.blocks)) {
    return "";
  }

  return draftContent.blocks
    .map((block) => {
      if (block.type === "unstyled") {
        return `<p>${block.text}</p>`;
      } else if (block.type === "header-one") {
        return `<h1>${block.text}</h1>`;
      } else if (block.type === "header-two") {
        return `<h2>${block.text}</h2>`;
      } else if (block.type === "header-three") {
        return `<h3>${block.text}</h3>`;
      } else if (block.type === "unordered-list-item") {
        return `<li>${block.text}</li>`;
      } else if (block.type === "ordered-list-item") {
        return `<li>${block.text}</li>`;
      } else {
        return `<p>${block.text}</p>`;
      }
    })
    .join("");
};

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, isArabic } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  // Use RTK Query to fetch service
  const {
    data: serviceResponse,
    isLoading: loading,
    error,
  } = useGetServiceQuery(id);
  const { data: settingsResponse } = useGetHomepageSettingsQuery();

  const service = serviceResponse?.service || null;
  const logoUrl = settingsResponse?.settings?.branding?.logo || null;

  // Combine main image and multiple images into one array for slider
  const allImages = useMemo(() => {
    if (!service) return [];
    
    const images = [];
    
    // Add main image first if it exists
    if (service.image_url) {
      images.push(service.image_url);
    }
    
    // Add multiple images if they exist
    if (service.multiple_images_urls && Array.isArray(service.multiple_images_urls)) {
      images.push(...service.multiple_images_urls);
    }
    
    return images;
  }, [service]);

  // Initialize AOS and track page visit
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-in-out",
      once: true,
      offset: 100,
    });
  }, []);

  // Track service page visit
  useEffect(() => {
    if (service) {
      trackServicePage(service.id, service.title);
    }
  }, [service]);

  // Set page title and meta tags
  useEffect(() => {
    if (service) {
      // Use setTimeout to ensure this runs after HeadTags component
      const timeoutId = setTimeout(() => {
        // Set page title - use meta_title if available, otherwise use service title
        const pageTitle = service.meta_title || service.title || "Service";
        document.title = pageTitle;

        // Remove ALL existing meta keywords tags (in case HeadTags or other components added them)
        const existingMetaKeywords = document.querySelectorAll(
          'meta[name="keywords"]'
        );
        existingMetaKeywords.forEach((tag) => tag.remove());

        // Remove existing meta description tags
        const existingMetaDescription = document.querySelectorAll(
          'meta[name="description"]'
        );
        existingMetaDescription.forEach((tag) => tag.remove());

        // Create new meta keywords tag with service keywords
        if (service.keywords || service.title) {
          const metaKeywords = document.createElement("meta");
          metaKeywords.setAttribute("name", "keywords");

          // Combine service title and keywords
          const keywords = service.keywords
            ? `${service.title}, ${service.keywords}`
            : service.title;

          metaKeywords.setAttribute("content", keywords);
          document.head.appendChild(metaKeywords);
        }

        // Create meta description tag - only use if service has specific meta_description
        // Don't fall back to general description - if no specific one, don't set it
        if (service.meta_description) {
          const metaDesc = document.createElement("meta");
          metaDesc.setAttribute("name", "description");
          metaDesc.setAttribute("content", service.meta_description);
          document.head.appendChild(metaDesc);
        }
      }, 100); // Small delay to ensure HeadTags runs first

      // Cleanup function
      return () => {
        clearTimeout(timeoutId);
        document.title = "Silver Fort";
        // Remove meta keywords on unmount
        const metaKeywords = document.querySelectorAll('meta[name="keywords"]');
        metaKeywords.forEach((tag) => tag.remove());
        // Remove meta description on unmount
        const metaDescription = document.querySelectorAll('meta[name="description"]');
        metaDescription.forEach((tag) => tag.remove());
      };
    }
  }, [service, settingsResponse]);

  const handleBackToServices = () => {
    navigate(getLocalizedPath("/services"));
  };

  const handleContactUs = () => {
    navigate(getLocalizedPath("/#contact"));
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  if (loading) {
    return (
      <div className="service-detail-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading service details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="service-detail-page">
        <div className="container">
          <div className="error-container">
            <h1>Service Not Found</h1>
            <p>
              The service you're looking for doesn't exist or has been removed.
            </p>
            <button className="btn" onClick={handleBackToServices}>
              Back to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-detail-page">
      <Header />
      <div className="container">
        {/* Main Content */}
        <div className="service-main-content">
          <div
            className="service-image-section"
            data-aos="fade-right"
            data-aos-delay="200"
          >
            {allImages.length > 0 ? (
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={10}
                slidesPerView={1}
                navigation={allImages.length > 1}
                pagination={{
                  enabled: allImages.length > 1,
                  clickable: true,
                  dynamicBullets: false,
                  type: "bullets",
                }}
                className="service-images-swiper"
                dir={isArabic ? "rtl" : "ltr"}
                key={isArabic ? "rtl" : "ltr"}
              >
                {allImages.map((imageUrl, index) => (
                  <SwiperSlide key={index}>
                    <div className="service-image-wrapper">
                      <img
                        src={imageUrl}
                        alt={`${service.title} - Image ${index + 1}`}
                        className="service-hero-image"
                        onError={(e) => {
                          e.target.style.display = "none";
                          if (e.target.nextSibling) {
                            e.target.nextSibling.style.display = "flex";
                          }
                        }}
                      />
                      <div
                        className="service-image-placeholder"
                        style={{ display: "none" }}
                      >
                        <div className="placeholder-content">
                          <div className="placeholder-icon">🔧</div>
                          <p>{t("common.noImageAvailable")}</p>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <div
                className="service-image-placeholder"
                style={{ display: "flex" }}
              >
                <div className="placeholder-content">
                  <div className="placeholder-icon">🔧</div>
                  <p>{t("common.noImageAvailable")}</p>
                </div>
              </div>
            )}
          </div>

          <div
            className="service-details"
            data-aos="fade-left"
            data-aos-delay="300"
          >
            <h1 className="service-title">
              {isArabic && service.title_ar ? service.title_ar : service.title}
            </h1>
            {service.category && (
              <div className="service-category">
                <span className="category-badge">
                  {isArabic && service.category.title_ar
                    ? service.category.title_ar
                    : service.category.title}
                </span>
              </div>
            )}
            <div className="service-description">
              <div
                dangerouslySetInnerHTML={{
                  __html: parseDraftContent(
                    isArabic && service.content_ar
                      ? service.content_ar
                      : service.content
                  ),
                }}
              />
            </div>

            <div
              className="service-cta"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="cta-content">
                <h2>
                  {service.cta_title && (isArabic ? service.cta_title_ar || service.cta_title : service.cta_title)
                    ? (isArabic ? service.cta_title_ar || service.cta_title : service.cta_title)
                    : t("services.needThisService")}
                </h2>
                <p>
                  {service.cta_description && (isArabic ? service.cta_description_ar || service.cta_description : service.cta_description)
                    ? (isArabic ? service.cta_description_ar || service.cta_description : service.cta_description)
                    : t("services.serviceDescription")}
                </p>

                <div className="cta-buttons">
                  {service.cta_button1_link ? (
                    <a
                      href={service.cta_button1_link}
                      className="btn btn-primary"
                      onClick={(e) => {
                        if (service.cta_button1_link.startsWith('/')) {
                          e.preventDefault();
                          navigate(service.cta_button1_link);
                        }
                      }}
                    >
                      {service.cta_button1_text && (isArabic ? service.cta_button1_text_ar || service.cta_button1_text : service.cta_button1_text)
                        ? (isArabic ? service.cta_button1_text_ar || service.cta_button1_text : service.cta_button1_text)
                        : t("contact.contactUsNow")}
                    </a>
                  ) : (
                    <button className="btn btn-primary" onClick={handleContactUs}>
                      {service.cta_button1_text && (isArabic ? service.cta_button1_text_ar || service.cta_button1_text : service.cta_button1_text)
                        ? (isArabic ? service.cta_button1_text_ar || service.cta_button1_text : service.cta_button1_text)
                        : t("contact.contactUsNow")}
                    </button>
                  )}
                  {service.cta_button2_link ? (
                    <a
                      href={service.cta_button2_link}
                      className="btn btn-secondary"
                      onClick={(e) => {
                        if (service.cta_button2_link.startsWith('/')) {
                          e.preventDefault();
                          navigate(service.cta_button2_link);
                        }
                      }}
                    >
                      {service.cta_button2_text && (isArabic ? service.cta_button2_text_ar || service.cta_button2_text : service.cta_button2_text)
                        ? (isArabic ? service.cta_button2_text_ar || service.cta_button2_text : service.cta_button2_text)
                        : t("services.viewAll")}
                    </a>
                  ) : (
                    <button
                      className="btn btn-secondary"
                      onClick={handleBackToServices}
                    >
                      {service.cta_button2_text && (isArabic ? service.cta_button2_text_ar || service.cta_button2_text : service.cta_button2_text)
                        ? (isArabic ? service.cta_button2_text_ar || service.cta_button2_text : service.cta_button2_text)
                        : t("services.viewAll")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
      {/* Floating Buttons */}
      <FloatingButtons />
    </div>
  );
};

export default ServiceDetail;
